import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ChevronRight, Zap, MapPin, Flag, ArrowLeft, Trophy, Users, Timer, CheckCircle2 } from 'lucide-react';
import { getF1Schedule, getF1DriverStandings, getF1ConstructorStandings, getF1LatestResults } from '@/lib/api';
import { useSEO } from '@/lib/useSEO';

const BORMULA_LOGO = "https://customer-assets.emergentagent.com/job_a6e857fc-9f8a-42a4-a234-830c8a3cc714/artifacts/98ozj9ol_Bormula%20Live%20Logo%201080x1350%20%281%29.png";

const TABS = [
  { id: 'schedule', label: 'Race Calendar', icon: Calendar },
  { id: 'drivers', label: 'Drivers', icon: Trophy },
  { id: 'constructors', label: 'Constructors', icon: Users },
  { id: 'latest', label: 'Last Race', icon: Flag },
];

// Team colors for constructor badges
const TEAM_COLORS = {
  'mercedes': { bg: 'bg-teal-500/20', text: 'text-teal-400' },
  'ferrari': { bg: 'bg-red-500/20', text: 'text-red-400' },
  'red_bull': { bg: 'bg-blue-500/20', text: 'text-blue-400' },
  'mclaren': { bg: 'bg-orange-500/20', text: 'text-orange-400' },
  'aston_martin': { bg: 'bg-green-500/20', text: 'text-green-400' },
  'alpine': { bg: 'bg-pink-500/20', text: 'text-pink-400' },
  'williams': { bg: 'bg-sky-500/20', text: 'text-sky-400' },
  'rb': { bg: 'bg-indigo-500/20', text: 'text-indigo-400' },
  'sauber': { bg: 'bg-emerald-500/20', text: 'text-emerald-400' },
  'haas': { bg: 'bg-gray-500/20', text: 'text-gray-400' },
  'cadillac': { bg: 'bg-yellow-500/20', text: 'text-yellow-400' },
};

const getTeamStyle = (constructorId) => {
  const id = constructorId?.toLowerCase() || '';
  for (const [key, style] of Object.entries(TEAM_COLORS)) {
    if (id.includes(key)) return style;
  }
  return { bg: 'bg-white/10', text: 'text-white/70' };
};

const formatRaceDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const isRacePast = (dateStr) => {
  if (!dateStr) return false;
  const raceDate = new Date(dateStr + 'T23:59:59');
  return raceDate < new Date();
};

const isNextRace = (races) => {
  const today = new Date();
  for (let i = 0; i < races.length; i++) {
    const raceDate = new Date(races[i].date + 'T00:00:00');
    if (raceDate >= today) return i;
  }
  return -1;
};

// ==================== Schedule Tab ====================
const ScheduleTab = ({ races }) => {
  const nextIdx = isNextRace(races);

  return (
    <div className="space-y-3">
      {races.map((race, i) => {
        const past = isRacePast(race.date);
        const isNext = i === nextIdx;
        const location = race.Circuit?.Location;

        return (
          <div
            key={race.round}
            className={`group flex flex-col lg:flex-row lg:items-center justify-between p-5 lg:p-6 rounded-2xl border transition-all ${
              isNext
                ? 'bg-[#CCFF00]/5 border-[#CCFF00]/40 ring-1 ring-[#CCFF00]/20'
                : past
                  ? 'bg-[#0a0a0a] border-white/5 opacity-60'
                  : 'bg-[#111111] border-white/5 hover:border-white/15'
            }`}
            data-testid={`race-${race.round}`}
          >
            <div className="flex items-center gap-5 mb-3 lg:mb-0">
              <div className={`text-3xl font-display font-bold w-12 text-center ${
                isNext ? 'text-[#CCFF00]' : past ? 'text-white/15' : 'text-white/20'
              }`}>
                {String(race.round).padStart(2, '0')}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1 flex-wrap">
                  <span className={`text-xs font-black uppercase tracking-widest ${isNext ? 'text-[#CCFF00]' : 'text-[#CCFF00]/70'}`}>
                    {location?.country || ''}
                  </span>
                  {isNext && (
                    <span className="px-2 py-0.5 bg-[#CCFF00] text-black text-[10px] font-black uppercase rounded animate-pulse">
                      Up Next
                    </span>
                  )}
                  {past && (
                    <CheckCircle2 className="w-3.5 h-3.5 text-white/30" />
                  )}
                </div>
                <h3 className={`text-lg lg:text-xl font-display font-bold uppercase ${isNext ? 'text-white' : ''}`}>
                  {race.raceName}
                </h3>
                <p className="text-white/40 text-sm mt-0.5 flex items-center gap-1.5">
                  <MapPin size={12} /> {race.Circuit?.circuitName}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-6 lg:gap-10 pl-[4.25rem] lg:pl-0">
              <div className="text-right">
                <div className="text-[10px] font-black uppercase tracking-widest text-white/30 mb-0.5">Race Day</div>
                <div className="font-display font-bold text-base">{formatRaceDate(race.date)}</div>
              </div>
              <ChevronRight className="hidden lg:block text-white/15" size={18} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ==================== Driver Standings Tab ====================
const DriverStandingsTab = ({ standings, round }) => {
  if (!standings.length) return <EmptyState text="No driver standings data available yet." />;

  const maxPoints = parseFloat(standings[0]?.points || 1);

  return (
    <div>
      <p className="text-xs font-black uppercase tracking-widest text-white/30 mb-6">After Round {round}</p>
      <div className="space-y-2">
        {standings.map((s) => {
          const pct = (parseFloat(s.points) / maxPoints) * 100;
          const team = s.Constructors?.[0];
          const teamStyle = getTeamStyle(team?.constructorId);

          return (
            <div
              key={s.Driver?.driverId}
              className="group flex items-center gap-4 p-4 bg-[#111111] border border-white/5 rounded-xl hover:border-white/15 transition-colors"
              data-testid={`driver-${s.position}`}
            >
              {/* Position */}
              <div className={`w-10 text-center font-display font-bold text-lg ${
                s.position === '1' ? 'text-[#CCFF00]' : s.position === '2' ? 'text-white/80' : s.position === '3' ? 'text-orange-400' : 'text-white/30'
              }`}>
                {s.position}
              </div>

              {/* Driver Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-display font-bold text-white uppercase text-sm lg:text-base">
                    {s.Driver?.givenName} {s.Driver?.familyName}
                  </span>
                  <span className={`px-2 py-0.5 text-[10px] font-black uppercase rounded ${teamStyle.bg} ${teamStyle.text}`}>
                    {team?.name || ''}
                  </span>
                </div>
                {/* Points bar */}
                <div className="mt-2 h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-[#CCFF00]/60 rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
                </div>
              </div>

              {/* Points */}
              <div className="text-right flex-shrink-0">
                <span className="font-display font-bold text-lg text-white">{s.points}</span>
                <span className="text-white/30 text-xs ml-1">pts</span>
              </div>

              {/* Wins */}
              {parseInt(s.wins) > 0 && (
                <div className="flex-shrink-0 flex items-center gap-1 text-[#CCFF00]">
                  <Trophy className="w-3.5 h-3.5" />
                  <span className="text-xs font-bold">{s.wins}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ==================== Constructor Standings Tab ====================
const ConstructorStandingsTab = ({ standings, round }) => {
  if (!standings.length) return <EmptyState text="No constructor standings data available yet." />;

  const maxPoints = parseFloat(standings[0]?.points || 1);

  return (
    <div>
      <p className="text-xs font-black uppercase tracking-widest text-white/30 mb-6">After Round {round}</p>
      <div className="space-y-3">
        {standings.map((s) => {
          const pct = (parseFloat(s.points) / maxPoints) * 100;
          const teamStyle = getTeamStyle(s.Constructor?.constructorId);

          return (
            <div
              key={s.Constructor?.constructorId}
              className="group p-5 bg-[#111111] border border-white/5 rounded-xl hover:border-white/15 transition-colors"
              data-testid={`constructor-${s.position}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-4">
                  <span className={`font-display font-bold text-2xl w-10 ${
                    s.position === '1' ? 'text-[#CCFF00]' : s.position === '2' ? 'text-white/80' : s.position === '3' ? 'text-orange-400' : 'text-white/30'
                  }`}>
                    {s.position}
                  </span>
                  <div>
                    <span className="font-display font-bold text-white uppercase text-lg">{s.Constructor?.name}</span>
                    <span className={`ml-3 px-2 py-0.5 text-[10px] font-black uppercase rounded ${teamStyle.bg} ${teamStyle.text}`}>
                      {s.Constructor?.nationality}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {parseInt(s.wins) > 0 && (
                    <div className="flex items-center gap-1 text-[#CCFF00]">
                      <Trophy className="w-4 h-4" />
                      <span className="text-sm font-bold">{s.wins} wins</span>
                    </div>
                  )}
                  <div className="text-right">
                    <span className="font-display font-bold text-2xl text-white">{s.points}</span>
                    <span className="text-white/30 text-xs ml-1">pts</span>
                  </div>
                </div>
              </div>
              <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full bg-[#CCFF00]/50 rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ==================== Latest Race Results Tab ====================
const LatestResultsTab = ({ race, results }) => {
  if (!race) return <EmptyState text="No race results available yet." />;

  return (
    <div>
      {/* Race Header */}
      <div className="mb-8 p-6 bg-[#111111] border border-white/5 rounded-2xl">
        <div className="flex items-center gap-2 text-[#CCFF00] text-xs font-black uppercase tracking-widest mb-2">
          <Flag className="w-4 h-4" /> Round {race.round}
        </div>
        <h3 className="font-display text-3xl font-bold uppercase text-white mb-1">{race.raceName}</h3>
        <p className="text-white/40 text-sm flex items-center gap-2">
          <MapPin className="w-3.5 h-3.5" /> {race.circuit} — {race.locality}, {race.country}
        </p>
        <p className="text-white/30 text-xs mt-2">{formatRaceDate(race.date)}</p>
      </div>

      {/* Results */}
      <div className="space-y-2">
        {results.map((r) => {
          const teamStyle = getTeamStyle(r.Constructor?.constructorId);
          const isPodium = parseInt(r.position) <= 3;

          return (
            <div
              key={r.position}
              className={`flex items-center gap-4 p-4 rounded-xl border transition-colors ${
                isPodium ? 'bg-[#111111] border-white/10' : 'bg-[#0d0d0d] border-white/5'
              }`}
              data-testid={`result-${r.position}`}
            >
              <div className={`w-10 text-center font-display font-bold text-lg ${
                r.position === '1' ? 'text-[#CCFF00]' : r.position === '2' ? 'text-white/80' : r.position === '3' ? 'text-orange-400' : 'text-white/30'
              }`}>
                {r.positionText || r.position}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`font-display font-bold uppercase text-sm ${isPodium ? 'text-white' : 'text-white/70'}`}>
                    {r.Driver?.givenName} {r.Driver?.familyName}
                  </span>
                  <span className={`px-2 py-0.5 text-[10px] font-black uppercase rounded ${teamStyle.bg} ${teamStyle.text}`}>
                    {r.Constructor?.name}
                  </span>
                </div>
                {r.FastestLap?.rank === '1' && (
                  <span className="text-purple-400 text-[10px] font-bold uppercase mt-1 inline-block">Fastest Lap</span>
                )}
              </div>
              <div className="flex items-center gap-4 text-right flex-shrink-0">
                <div>
                  <span className="font-display font-bold text-white">{r.points}</span>
                  <span className="text-white/30 text-xs ml-1">pts</span>
                </div>
                <div className="text-white/30 text-xs w-20 text-right">
                  {r.status === 'Finished' ? (r.Time?.time || '') : r.status}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const EmptyState = ({ text }) => (
  <div className="text-center py-24">
    <Timer className="w-12 h-12 text-white/15 mx-auto mb-4" />
    <p className="text-white/40 text-lg">{text}</p>
  </div>
);

// ==================== Main Page ====================
export default function CalendarPage() {
  const [activeTab, setActiveTab] = useState('schedule');
  const [schedule, setSchedule] = useState({ season: '', races: [] });
  const [driverStandings, setDriverStandings] = useState({ standings: [], round: '0' });
  const [constructorStandings, setConstructorStandings] = useState({ standings: [], round: '0' });
  const [latestResults, setLatestResults] = useState({ race: null, results: [] });
  const [loading, setLoading] = useState(true);

  useSEO({
    title: '2026 F1 Race Calendar & Standings',
    description: 'Live F1 race calendar, driver standings, constructor standings, and latest race results — updated automatically.',
    url: 'https://bormula.com/calendar',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sched, drivers, constructors, latest] = await Promise.all([
        getF1Schedule().catch(() => ({ season: '', races: [] })),
        getF1DriverStandings().catch(() => ({ standings: [], round: '0' })),
        getF1ConstructorStandings().catch(() => ({ standings: [], round: '0' })),
        getF1LatestResults().catch(() => ({ race: null, results: [] })),
      ]);
      setSchedule(sched);
      setDriverStandings(drivers);
      setConstructorStandings(constructors);
      setLatestResults(latest);
    } catch (e) {
      console.error('Failed to fetch F1 data:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const raceCount = schedule.races.length;
  const completedCount = schedule.races.filter(r => isRacePast(r.date)).length;

  return (
    <div className="min-h-screen bg-black font-sans" data-testid="calendar-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 w-full z-50 bg-black/80 backdrop-blur-xl border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img src={BORMULA_LOGO} alt="Bormula" className="h-10 w-10 object-contain" />
            <span className="font-display font-bold text-2xl tracking-tighter">BORMULA</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-white/60 hover:text-[#CCFF00] font-bold uppercase text-xs tracking-widest transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft size={16} /> Back to Home
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-10 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#CCFF00]/10 blur-[150px] -z-10" />
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <Calendar className="text-[#CCFF00]" size={48} />
            <div>
              <h1 className="text-5xl md:text-7xl font-display font-bold uppercase tracking-tighter">
                {schedule.season || '2026'} <span className="text-[#CCFF00]">Season</span>
              </h1>
              <p className="text-white/40 text-lg mt-2">Live race calendar, standings & results</p>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-4 mt-10 max-w-xl">
            <div className="bg-[#111111] border border-white/5 p-5 rounded-2xl text-center">
              <div className="text-3xl font-display font-bold text-[#CCFF00]">{raceCount}</div>
              <div className="text-[10px] font-black uppercase tracking-widest text-white/40 mt-1">Races</div>
            </div>
            <div className="bg-[#111111] border border-white/5 p-5 rounded-2xl text-center">
              <div className="text-3xl font-display font-bold text-white">{completedCount}</div>
              <div className="text-[10px] font-black uppercase tracking-widest text-white/40 mt-1">Completed</div>
            </div>
            <div className="bg-[#111111] border border-white/5 p-5 rounded-2xl text-center">
              <div className="text-3xl font-display font-bold text-white">{driverStandings.round}</div>
              <div className="text-[10px] font-black uppercase tracking-widest text-white/40 mt-1">Round</div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex flex-wrap gap-2 mt-10" data-testid="tab-nav">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-2 px-5 py-3 rounded-full text-xs font-black uppercase tracking-widest transition-all ${
                  activeTab === id
                    ? 'bg-[#CCFF00] text-black'
                    : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                }`}
                data-testid={`tab-${id}`}
              >
                <Icon size={14} /> {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tab Content */}
      <section className="px-6 pb-24">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="spinner" />
            </div>
          ) : (
            <>
              {activeTab === 'schedule' && <ScheduleTab races={schedule.races} />}
              {activeTab === 'drivers' && <DriverStandingsTab standings={driverStandings.standings} round={driverStandings.round} />}
              {activeTab === 'constructors' && <ConstructorStandingsTab standings={constructorStandings.standings} round={constructorStandings.round} />}
              {activeTab === 'latest' && <LatestResultsTab race={latestResults.race} results={latestResults.results} />}
            </>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/10 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex items-center gap-2">
            <img src={BORMULA_LOGO} alt="Bormula" className="h-8 w-8 object-contain" />
            <span className="font-display font-bold text-xl tracking-tighter uppercase">Bormula</span>
          </div>
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-white/20">
            Data via Jolpica F1 API &middot; &copy; 2024 BORMULA MEDIA GROUP
          </div>
        </div>
      </footer>
    </div>
  );
}
